import React from "react";
import { Helmet } from "react-helmet-async";
import { SITE } from "@/lib/site";

/**
 * Seo — unique title, description, canonical, Open Graph + Twitter meta.
 * Use ONE per page. Pass path (e.g. "/mca-database") for canonical.
 */
export default function Seo({ title, description, keywords, path = "/", image, noindex = false, jsonLd = null }) {
  const canonical = `${SITE.url}${path}`;
  const ogImage = image || `${SITE.url}/pwa-icon-512.png`;
  return (
    <Helmet>
      <title>{title}</title>
      <meta name="description" content={description} />
      {keywords && <meta name="keywords" content={keywords} />}
      <link rel="canonical" href={canonical} />
      {noindex ? <meta name="robots" content="noindex,nofollow" /> : <meta name="robots" content="index,follow,max-image-preview:large" />}
      <meta property="og:type" content="website" />
      <meta property="og:site_name" content={SITE.brand} />
      <meta property="og:title" content={title} />
      <meta property="og:description" content={description} />
      <meta property="og:url" content={canonical} />
      <meta property="og:image" content={ogImage} />
      <meta name="twitter:card" content="summary_large_image" />
      <meta name="twitter:title" content={title} />
      <meta name="twitter:description" content={description} />
      <meta name="twitter:image" content={ogImage} />
      {jsonLd && (
        <script type="application/ld+json">{JSON.stringify(jsonLd)}</script>
      )}
    </Helmet>
  );
}

// Convenience: build a WebPage + BreadcrumbList combo
export function pageSchema({ title, description, path, breadcrumbs = [] }) {
  const items = breadcrumbs.map((b, i) => ({
    "@type": "ListItem",
    position: i + 1,
    name: b.name,
    item: `${SITE.url}${b.path}`,
  }));
  return [
    {
      "@context": "https://schema.org",
      "@type": "WebPage",
      name: title,
      description,
      url: `${SITE.url}${path}`,
      isPartOf: { "@type": "WebSite", name: SITE.brand, url: SITE.url },
    },
    items.length > 0 && {
      "@context": "https://schema.org",
      "@type": "BreadcrumbList",
      itemListElement: items,
    },
  ].filter(Boolean);
}
